import useResizeObserver from "@react-hook/resize-observer";
import * as d3 from "d3";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import stateInfo from "../data/state-info.json";

const IndiaMapInteractive = () => {
  const containerRef = useRef();
  const svgRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 700 });
  const [geoData, setGeoData] = useState(null);
  const [hoveredState, setHoveredState] = useState(null);
  const navigate = useNavigate();

  useResizeObserver(containerRef, (entry) => {
    setDimensions({
      width: entry.contentRect.width,
      height: entry.contentRect.width * 1.1,
    });
  });

  useEffect(() => {
    fetch("/geoBoundaries-IND-ADM1.geojson")
      .then((res) => res.json())
      .then((data) => {
        setGeoData(data);
      })
      .catch((err) => console.error("❌ GeoJSON load error:", err));
  }, []);

  const normalizeName = (name) =>
    name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const hoveredData = hoveredState
    ? stateInfo.find(
        (s) => normalizeName(s.name) === normalizeName(hoveredState)
      )
    : null;

  useEffect(() => {
    if (!geoData || !geoData.features) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const { width, height } = dimensions;

    const projection = d3
      .geoIdentity()
      .reflectY(true)
      .fitSize([width, height], geoData);

    const path = d3.geoPath().projection(projection);
    const color = d3.scaleOrdinal(d3.schemePastel1);

    svg
      .attr("width", width)
      .attr("height", height)
      .attr("aria-label", "Interactive map of India")
      .style("overflow", "visible")
      .style("background", "transparent");

    svg
      .selectAll("path")
      .data(geoData.features)
      .join("path")
      .attr("d", path)
      .attr("fill", (_, i) => color(i))
      .attr("stroke", "#666")
      .attr("stroke-width", 0.6)
      .attr("aria-label", (d) => d.properties.shapeName)
      .style("cursor", "pointer")
      .on("click", (_, d) => {
        const stateName = normalizeName(d.properties.shapeName);
        navigate(`/state/${stateName}`);
      })
      .on("mouseover", function (event, d) {
        setHoveredState(d.properties.shapeName);
        d3.select(this)
          .transition()
          .duration(0) // ⚡ Fast hover
          .attr("fill", "#ff9933")
          .attr("stroke", "#000")
          .attr("stroke-width", 1.5);
      })
      .on("mouseout", function (_, d, i) {
        setHoveredState(null);
        d3.select(this)
          .transition()
          .duration(0)
          .attr("fill", (_, i) => color(i))
          .attr("stroke", "#666")
          .attr("stroke-width", 0.6);
      });
  }, [geoData, dimensions, hoveredState, navigate]);

  return (
    <motion.div
      ref={containerRef}
      className="w-full max-w-5xl mx-auto relative"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* Title and Hint */}
<div className="w-full max-w-5xl mx-auto text-left px-4 mb-4">
  <div className="inline-block bg-white/90 px-4 py-2 rounded shadow border">
    <h2 className="text-lg md:text-xl font-bold text-orange-700">
      भारत का इंटरेक्टिव राज्य मानचित्र 🗺️
    </h2>
    <p className="text-xs text-gray-600 mt-1 italic">
      राज्य पर क्लिक करें — मुख्यमंत्री, विधायक और जन मुद्दों को देखें
    </p>
  </div>
</div>


      {/* SVG Map */}
      <svg ref={svgRef} className="w-full h-auto" />

      {/* Tooltip */}
      {hoveredData && (
        <div className="absolute top-20 left-4 bg-white shadow-lg rounded-lg p-3 z-50 text-sm border border-gray-200 pointer-events-none">
          <p className="font-semibold text-green-800">{hoveredData.name}</p>
          <p>👨‍⚖️ CM: {hoveredData.cm}</p>
          <p>🏳️‍🌈 Party: {hoveredData.rulingParty}</p>
          <p>🧑‍🏫 MLAs: {hoveredData.mlaCount}</p>
          <p>📢 Issues: {hoveredData.issueCount}</p>
        </div>
      )}
    </motion.div>
  );
};

export default IndiaMapInteractive;
